import {
    DeliveryType,
    IFormikSelectOptionsProps,
    IDeliveryFormValues,
    IInitialDeliveryFormValues, IMeetingPlanningOrderConfirmationModal, IMeetingPlanningOrderCompleteModal
} from "../../Types/MeetingPlanning/deliveryForm"
import * as yup from "yup"
import { DateTime } from "luxon"
import { ReactLocalization } from "@fluent/react/esm/localization"
import {
    BuyerParty,
    ICanteen,
    IMonetaryAmount,
    IMyOrderDetails, IOrderDelivery, IOrganizer,
    OrderPaymentDetails,
    OrderType,
    PaymentMethodType
} from "../../Types/appTypes"

export const TEXT_FIELD_MAX_LENGTH: number = 50
export const OTHER_MESSAGE_MAX_LENGTH: number = 500
export const OTHER_LOCATION_TRANSLATE_KEY: string = "other-location"
export const MIN_SEATS: number = 1
export const MAX_SEATS: number = 999
export const FIVE_MINUTES: number = 5
export const DIGITS_ONLY_REGEX = /^\d*$/
export const THREE_SECONDS = 3000

export const SELECT_OPTIONS: IFormikSelectOptionsProps[] = [
    {
        label: "delivery-type-option-canteen",
        value: DeliveryType.CANTEEN
    },
    {
        label: "delivery-type-option-pick-up",
        value: DeliveryType.PICK_UP
    },
    {
        label: "delivery-type-option-delivery",
        value: DeliveryType.DELIVERY
    }
]

export const DEFAULT_INITIAL_DELIVERY_FORM: IInitialDeliveryFormValues = {
    meetingName: "",
    fromDate: "",
    toDate: "",
    deliveryType: "",
    deliveryLocation: "",
    hasOtherDeliveryLocation: false,
    otherDeliveryLocation: "",
    seats: 0
}

export const basketDeliveryFormSchema = (l10n: ReactLocalization) => ({
    deliveryDate: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-date-required",
                undefined,
                "Leveringsdato er påkrævet")),
    deliveryTime: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-delivery-time-required",
                undefined,
                "Leveringstidspunkt er påkrævet")),
    meetingName: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-name-required",
                undefined,
                "Mødenavn er påkrævet"))
        .max(
            TEXT_FIELD_MAX_LENGTH,
            l10n.getString(
                "delivery-form-meeting-field-name-max-length",
                { maxLength: TEXT_FIELD_MAX_LENGTH },
                `Maksimal længde for mødenavn er ${TEXT_FIELD_MAX_LENGTH} tegn`
            )
        ),
    seats: yup
        .string()
        .required(
            l10n.getString(
                "delivery-form-meeting-field-seats-min-length",
                { minLength: MIN_SEATS },
                `Minimum antal pladser er ${MIN_SEATS}`
            )
        ).max(
            MAX_SEATS,
            l10n.getString(
                "delivery-form-meeting-field-seats-max-length",
                { maxLength: MAX_SEATS },
                `Maksimalt antal pladser er ${MAX_SEATS}`
            )
        ),
    deliveryType: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-delivery-type-required",
                undefined,
                "Leveringstype er påkrævet")),
    deliveryLocation: yup.string().when("deliveryType", {
        is: DeliveryType.DELIVERY,
        then: (schema) => schema.required(l10n.getString("delivery-form-delivery-location-field-required", undefined, "Leveringssted er påkrævet")),
        otherwise: (schema) => schema.notRequired()
    }).max(
        TEXT_FIELD_MAX_LENGTH,
        l10n.getString(
            "delivery-form-meeting-field-other-location-max-length",
            { maxLength: TEXT_FIELD_MAX_LENGTH },
            `Den maksimale længde for Andet leveringssted er ${TEXT_FIELD_MAX_LENGTH} tegn`
        )
    ),
    otherMessage: yup
        .string()
        .max(
            OTHER_MESSAGE_MAX_LENGTH,
            l10n.getString(
                "delivery-form-meeting-field-name-max-length",
                { maxLength: OTHER_MESSAGE_MAX_LENGTH },
                `Maksimal længde for mødenavn er ${OTHER_MESSAGE_MAX_LENGTH} tegn`
            )
        )
})

export const initialDeliveryFormSchema = (l10n: ReactLocalization) => ({
    meetingName: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-name-required",
                undefined,
                "Mødenavn er påkrævet"))
        .max(
            TEXT_FIELD_MAX_LENGTH,
            l10n.getString(
                "delivery-form-meeting-field-name-max-length",
                { maxLength: TEXT_FIELD_MAX_LENGTH },
                `Maksimal længde for mødenavn er ${TEXT_FIELD_MAX_LENGTH} tegn`
            )
        ),
    fromDate: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-from-date-required",
                undefined,
                "Mødenavn er påkrævet")),
    toDate: yup
        .string()
        .required(
            l10n.getString(
                "delivery-form-meeting-field-to-date-required",
                undefined,
                "Slutdato er påkrævet"
            )
        )
        .test(
            "is-after-fromDate",
            l10n.getString(
                "delivery-form-meeting-field-from-date-should-be-before-to-date",
                undefined,
                "Startdato skal være før slutdato"
            ),
            function(toDateValue) {
                const { fromDate } = this.parent
                if (!fromDate || !toDateValue) {
                    return true
                }
                return DateTime.fromISO(fromDate) < DateTime.fromISO(toDateValue)
            }
        ),
    deliveryType: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-delivery-type-required",
                undefined,
                "Leveringstype er påkrævet")),
    seats: yup
        .number()
        .min(
            MIN_SEATS,
            l10n.getString(
                "delivery-form-meeting-field-seats-min-length",
                { minLength: MIN_SEATS },
                `Minimum antal pladser er ${MIN_SEATS}`
            )
        ).max(
            MAX_SEATS,
            l10n.getString(
                "delivery-form-meeting-field-seats-max-length",
                { maxLength: MAX_SEATS },
                `Maksimalt antal pladser er ${MAX_SEATS}`
            )
        ),
    deliveryLocation: yup.string().when("deliveryType", {
        is: DeliveryType.DELIVERY,
        then: (schema) => schema.required(l10n.getString("delivery-form-delivery-location-field-required", undefined, "Leveringssted er påkrævet")),
        otherwise: (schema) => schema.notRequired()
    }),
    otherDeliveryLocation: yup.string().max(
        TEXT_FIELD_MAX_LENGTH,
        l10n.getString(
            "delivery-form-meeting-field-other-location-max-length",
            { maxLength: TEXT_FIELD_MAX_LENGTH },
            `Den maksimale længde for Andet leveringssted er ${TEXT_FIELD_MAX_LENGTH} tegn`
        )
    ),
    // TODO: Need clarify this logic with customer
    //     .when('hasOtherDeliveryLocation', {
    //     is: true,
    //     then: (schema) =>
    //         schema.required(
    //             l10n.getString(
    //                 "delivery-form-delivery-other-location-field-required",
    //                 undefined,
    //                 "Andet leveringssted er påkrævet"
    //             )
    //         ),
    //     otherwise: (schema) => schema.notRequired(),
    // }),
    hasOtherDeliveryLocation: yup.boolean().default(false)
})

export const deliveryFormSchema = (l10n: ReactLocalization) => ({
    deliveryTime: yup
        .string()
        .required(
            l10n.getString("delivery-form-meeting-field-delivery-time-required",
                undefined,
                "Leveringstidspunkt er påkrævet")),
    extraServices: yup
        .array()
        .of(
            yup.object({
                id: yup.number().required(),
                amount: yup.number().required().min(1)
            })
        )
        .default([])
})

export const basketDeliveryFormValues = () => ({
    deliveryDate: "",
    deliveryTime: "",
    meetingName: "",
    seats: 0,
    deliveryType: "",
    deliveryLocation: "",
    otherMessage: ""
})

export const initialDeliveryFormValues = (formValues: IInitialDeliveryFormValues | null) => ({
    meetingName: formValues?.meetingName || "",
    fromDate: formValues?.fromDate || "",
    toDate: formValues?.toDate || "",
    deliveryType: formValues?.deliveryType || "",
    deliveryLocation: formValues?.deliveryLocation || "",
    hasOtherDeliveryLocation: formValues?.hasOtherDeliveryLocation || false,
    otherDeliveryLocation: formValues?.otherDeliveryLocation || "",
    seats: formValues?.seats || 0
})

export const deliveryFormValues = (formValues: IDeliveryFormValues | null) => ({
    isCompleted: formValues?.isCompleted || false,
    deliveryTime: formValues?.deliveryTime || "",
    extraServices: formValues?.extraServices || [],
    price: formValues?.price || 0
})

export const BILL_RECEIVERS_OPTIONS = [{
    label: "FacilityNet ApS",
    value: "1"
}]

export const MEETING_ORDERS_KITCHEN_MOCK = {
    id: 2,
    name: "The Green Kitchen",
    phoneNumber: "004561305045",
    email: "theKitchen@domain.com",
    streetName: "Nørregade",
    streetNumber: "25",
    postalCode: "1100",
    city: "København K",
    address: {
        streetName: "Nørregade",
        streetNumber: "25",
        postalCode: "1100",
        city: "København K"
    },
    sellerSupplierParty: {
        uid: "81007e02-1520-4b17-835c-04134725d22e",
        name: "Kitchen supplier"
    }
}

export const MEETING_ORDERS_PAYMENT: OrderPaymentDetails = {
    paymentMethod: {
        value: PaymentMethodType.INVOICE,
        translationKey: "payment-method-salary-reduction",
        showSalesConditions: false,
        icon: {
            "type": "IMAGE_URL",
            "url": "https://facilitynet.dk/remote/gopay/payment-icons/payroll.svg"
        }
    },
    method: PaymentMethodType.INVOICE,
    status: "INVOICED",
    isCaptured: true,
    isOnlinePayment: true,
    bankAccountStatement: "",
    remoteIp: "",
    toBePaidAsPrivate: {
        amount: 10000,
        currency: "DKK",
        scale: 2,
        formatted: "100,00 kr"
    },
    toBePaidAsCompany: {
        amount: 10000,
        currency: "DKK",
        scale: 2,
        formatted: "100,00 kr"
    }
}

export const MEETING_ORDERS_CUSTOMER_MOCK: IOrganizer = {
    id: 12345,
    uid: "fa008b24-34a2-493e-a85b-bd145582c0cf",
    initials: "jh",
    firstname: "John",
    lastname: "Hansen",
    name: "John Hansen",
    email: "abc@domain.com",
    businessPhone: "004561305045",
    mobilePhone: "004561305045",
    departmentId: 100,
    department: "HR",
    customerTypeId: 10510,
    usertypeId: 1,
    organizerType: "CREATOR",
    companyId: 0,
    orderBaseId: 0,
}

export const MEETING_DELIVERY_MOCK: IOrderDelivery = {
    orderNote: "Ønsker ekstra kaffe...",
    sittings: 8,
    deliveryLocation: {
        name: "Lokale 101"
    },
    deliveryType: "PICK_UP",
    deliveryTime: "2025-05-26T12:30:00",
    orderLines: [
        {
            id: 6651,
            uid: "833",
            items: 1,
            price: {
                "amount": 3500,
                "scale": 2,
                "currency": "DKK",
                "formatted": "DKK 35.00"
            },
            imageUrl: "",
            name: "Ugens ret torsdag",
            description: "Ugens ret torsdag",
            buyerParty: BuyerParty.PRIVATE
        },
        {
            id: 6652,
            uid: "834",
            items: 2,
            price: {
                "amount": 6000,
                "scale": 2,
                "currency": "DKK",
                "formatted": "DKK 60.00"
            },
            imageUrl: "",
            name: "Frokostbuffet",
            description: "Ugens ret torsdag",
            buyerParty: BuyerParty.COMPANY
        },
    ],
}

export const MEETING_ORDER_MOCK: IMyOrderDetails = {
    id: 10050,
    uid: "6015f34a-b794-4acb-8e23-e36d4e85dace",
    displayName: "GoPay #10050",
    permaLink: "https://facilitynet.io/orders/byUUID/53f6557e-d085-4054-935a-d68a3d7f85bf",
    created: "2025-05-26",
    cateringDetails: {
        cateringOrderId: 10050,
        uid: "6015f34a-b794-4acb-8e23-e36d4e85dace",
        meetingStartTime: "2025-05-26 12:30:00",
        meetingEndTime: "2025-05-26 12:30:00",
        orderNote: "Ønsker ekstra kaffe...",
        sittings: 8,
        deliveryLocation: "Lokale 101",
        deliveryTime: "2025-05-26T12:30:00",
        bookedTime: "2025-05-26",
        isLocked: true,
        isContantPayment: true,
        isConfirmed: true,
        deliveryType: "DELIVERY",
        totalAmount: 1000,
        vat: 12345678,
        canteen: {
            id: 2,
            name: "The Green Kitchen",
            phoneNumber: "004561305045",
            email: "theKitchen@domain.com",
            streetName: "Nørregade",
            streetNumber: "25",
            postalCode: "1100",
            city: "København K",
            address: {
                streetName: "Nørregade",
                streetNumber: "25",
                postalCode: "1100",
                city: "København K"
            },
            sellerSupplierParty: {
                uid: "81007e02-1520-4b17-835c-04134725d22e",
                name: "Kitchen supplier"
            }
        },
        created: "2025-05-26 12:30:00",
        updated: "2025-05-26 12:30:00",
        updatedByUserId: 1
    },
    orderType: OrderType.MEETING_SERVICE,
    qrReceiptUrl: "https://facilitynet.io/orders/byUUID/53f6557e-d085-4054-935a-d68a3d7f85bf",
    price: {
        amount: 10000,
        currency: "DKK",
        scale: 2,
        formatted: "100,00 kr"
    },
    kitchen: MEETING_ORDERS_KITCHEN_MOCK,
    payment: {
        method: PaymentMethodType.MOBILE_PAY
    },
    paymentDetails: MEETING_ORDERS_PAYMENT,
    deliveries: [MEETING_DELIVERY_MOCK]
}

export const MEETING_ORDERS_MOCK: IMyOrderDetails[] = [
    MEETING_ORDER_MOCK
]

export const CONFIRM_MODAL_INITIAL_DATA: IMeetingPlanningOrderConfirmationModal = {
    isOpen: false,
    userId: null,
    formData: null
}

export const COMPLETE_MODAL_INITIAL_DATA: IMeetingPlanningOrderCompleteModal = {
    isOpen: false,
    isEdit: false,
    isResourceBookingOrder: false,
    deliveryDate: '',
    deliveryTime: '',
}
